import React, { useEffect, useState } from "react";
import {
  IonItem,
  IonLabel,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonAccordionGroup,
  IonAccordion,
  IonButton,
  IonContent,
  IonPage,
} from "@ionic/react";
import { useParams, useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { listMovements as listMovements, getPeopleCountHistoryCosmos } from "../services/api";
import PeopleCountChart from "../components/PeopleCountChart";

const formatDay = (dayStr: string, separator: string) => {
  if (!dayStr || dayStr.length < 8) return dayStr;
  const year = dayStr.slice(0, 4);
  const month = dayStr.slice(4, 6);
  const day = dayStr.slice(6, 8);
  return `${day}${separator}${month}${separator}${year}`;
};

// Função utilitária para extrair só o dia (YYYYMMDD) do campo date
function getDayKey(dateStr: string) {
  return dateStr?.slice(0, 8); // "20250827_1804" -> "20250827"
}

const MovementsPage: React.FC = () => {
  const { streamId, cameraName } = useParams<{ streamId: string; cameraName: string }>();
  const decodedCameraName = decodeURIComponent(cameraName);
  const { getAccessTokenSilently } = useAuth0();
  const [entryExitData, setEntryExitData] = useState<
    { recorded_at: string; entries: number; exits: number }[]
  >([]);
  const [chartDataByDay, setChartDataByDay] = useState<Record<string, { loading: boolean; data: any[]; error?: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (!streamId) return;

    const fetchEntryExit = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const data = await listMovements(streamId, token);
        setEntryExitData(data);
        setError(null);
      } catch (err: any) {
        setEntryExitData([]);
        setError(err.message || "Erro ao carregar entradas/saídas");
      } finally {
        setLoading(false);
      }
    };

    fetchEntryExit();
  }, [streamId, getAccessTokenSilently]);

  const handleAccordionChange = async (day: string) => {
    if (chartDataByDay[day]?.data) return;
    setChartDataByDay((prev) => ({
      ...prev,
      [day]: { loading: true, data: [] },
    }));
    try {
      const token = await getAccessTokenSilently();
      const cosmosData = await getPeopleCountHistoryCosmos(streamId, formatDay(day, "-"), token);
      setChartDataByDay((prev) => ({
        ...prev,
        [day]: { loading: false, data: cosmosData },
      }));
    } catch (err: any) {
      setChartDataByDay((prev) => ({
        ...prev,
        [day]: { loading: false, data: [], error: err.message || "Erro ao carregar gráfico" },
      }));
    }
  };

  // Agrupa apenas saídas por dia (entradas = saídas)
  const entryExitByDay = entryExitData.reduce((acc, item) => {
    const dayKey = getDayKey(item.recorded_at?.trim());
    if (!dayKey) return acc;
    if (!acc[dayKey]) acc[dayKey] = { exits: 0 };
    acc[dayKey].exits += item.exits;
    return acc;
  }, {} as Record<string, { exits: number }>);

  const BackButton = (
    <div style={{ textAlign: "center", marginTop: 16 }}>
      <IonButton
        size="small"
        color="primary"
        onClick={() => history.push("/home")}
      >
        Voltar para o início
      </IonButton>
    </div>
  );

  if (loading) {
    return (
      <>
        {BackButton}
        <div style={{ textAlign: "center", marginTop: 40 }}>
          Carregando entradas/saídas...
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {BackButton}
        <div style={{ textAlign: "center", marginTop: 40, color: "red" }}>
          {error}
        </div>
      </>
    );
  }

  const allDays = Object.keys(entryExitByDay).sort();

  return (
    <IonPage>
      <IonContent fullscreen>
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Câmera: {decodedCameraName}</IonCardTitle>
            <IonButton
              size="small"
              color="primary"
              style={{ marginTop: 8 }}
              onClick={() => history.push("/home")}
            >
              Voltar para o início
            </IonButton>
          </IonCardHeader>

          <IonCardContent>
            <IonAccordionGroup
              onIonChange={(e) => {
                const day = e.detail.value as string | undefined;
                if (day) handleAccordionChange(day);
              }}
            >
              {allDays.length === 0 ? (
                <IonItem>
                  <IonLabel>Nenhum dado de entradas/saídas encontrado.</IonLabel>
                </IonItem>
              ) : (
                allDays.map((day) => {
                  const dayEntryExit = entryExitByDay[day];
                  const expanded = !!chartDataByDay[day]?.data?.length || chartDataByDay[day]?.loading;
                  return (
                    <IonAccordion value={day} key={day}>
                      <IonItem slot="header" color="light">
                        <IonLabel>
                          <strong>Data:</strong> {formatDay(day, "/")}
                          &nbsp;|&nbsp;<strong>Entradas/Saídas:</strong> {dayEntryExit.exits}
                        </IonLabel>
                      </IonItem>
                      <div slot="content">
                        {chartDataByDay[day]?.loading && (
                          <div style={{ textAlign: "center", margin: 24 }}>Carregando gráfico...</div>
                        )}
                        {chartDataByDay[day]?.error && (
                          <div style={{ color: "red", textAlign: "center", margin: 24 }}>
                            {chartDataByDay[day].error}
                          </div>
                        )}
                        {chartDataByDay[day]?.data?.length > 0 && (
                          <PeopleCountChart data={chartDataByDay[day].data} />
                        )}
                      </div>
                    </IonAccordion>
                  );
                })
              )}
            </IonAccordionGroup>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage >
  );
};

export default MovementsPage;